import { Link } from "react-router-dom";
import { Check, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import CompletionBadge from "./CompletionBadge";
import {
  useProfileCompletion,
  type ProfileRow,
  TIER_META,
} from "@/hooks/useProfileCompletion";

interface Props {
  profile: ProfileRow | null;
  className?: string;
  /** Path users go to in order to edit fields. Defaults to /profile?tab=settings. */
  settingsHref?: string;
}

const TIER_TITLES = ["Essentials", "Profile completion", "Power user"];
const TIER_BLURB = [
  "What other shareholders need to recognize you.",
  "Credibility, focus, and where you serve.",
  "Unlock better matching and trust signals.",
];

const ProfileCompletionCard = ({ profile, className = "", settingsHref = "/profile?tab=settings" }: Props) => {
  const { tier, percent, tierStatus, firstMissing } = useProfileCompletion(profile);

  if (tier === 3) {
    return (
      <div className={`rounded-2xl border border-primary/30 bg-primary/5 p-5 ${className}`}>
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-primary" />
          <div className="flex-1">
            <h3 className="font-display text-sm font-semibold">Power user profile complete</h3>
            <p className="text-xs text-muted-foreground">
              You'll show up first in matching, partner search, and grant recommendations.
            </p>
          </div>
          <CompletionBadge tier={3} />
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border border-border bg-card p-5 shadow-card ${className}`}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-display text-sm font-semibold">
              Complete your profile
            </h3>
            <CompletionBadge tier={tier} size="sm" />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {percent}% complete · next up: {firstMissing?.label ?? "—"}
          </p>
        </div>
        <Link to={settingsHref}>
          <Button size="sm" variant="outline">
            Continue
            <ChevronRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </Link>
      </div>

      <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>

      <ol className="space-y-3">
        {tierStatus.map((t, idx) => {
          const tierNum = (idx + 1) as 1 | 2 | 3;
          const total = t.spec.length;
          const done = t.done.length;
          const isActive = !t.complete && (idx === 0 || tierStatus[idx - 1].complete);
          return (
            <li
              key={idx}
              className={`rounded-xl border p-3 ${
                t.complete
                  ? "border-primary/30 bg-primary/5"
                  : isActive
                    ? "border-primary/40 bg-secondary/40"
                    : "border-border bg-background/40 opacity-70"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                      t.complete
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {t.complete ? <Check className="h-3.5 w-3.5" /> : tierNum}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{TIER_TITLES[idx]}</p>
                      <span className="text-[11px] text-muted-foreground">
                        {done}/{total}
                      </span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {t.complete ? `Unlocks: ${TIER_META[tierNum].chipLabel}` : TIER_BLURB[idx]}
                    </p>
                  </div>
                </div>
              </div>
              {!t.complete && isActive && t.missing.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5 pl-9">
                  {t.missing.map((f) => (
                    <span
                      key={f.id}
                      className="rounded-full border border-border bg-background px-2 py-0.5 text-[10px] text-muted-foreground"
                    >
                      {f.label}
                    </span>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default ProfileCompletionCard;